import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { ChallengeCredentials } from "../../types/auth";
import ChallengeForm from "../../components/auth/ChallengeForm";
import { 
  confirmSignUp, 
  resendConfirmationCode,
  clearConfirmationError,
  selectConfirmationState,
  selectResendState,
  selectPendingConfirmationEmail,
  selectAuthError
} from "../../redux/auth/authSlice";
import { useNavigate } from "react-router-dom";
import { getAccountSession, getUserAccount } from "../../utils/auth";
import Preloader from "../../components/Preloader";
import { CheckCircle, ArrowRight } from "lucide-react";

export const ChallengePage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Redux selectors
  const confirmationState = useSelector(selectConfirmationState);
  const resendState = useSelector(selectResendState);
  const pendingEmail = useSelector(selectPendingConfirmationEmail);
  const authError = useSelector(selectAuthError);
  
  // State for enhanced user experience
  const [userEmail, setUserEmail] = useState<string>("");
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState<number | null>(null);

  // Enhanced error and success message handling
  const challengeErrorMessage = confirmationState.isError 
    ? (authError || "Failed to verify email") 
    : "";
  const resendCodeErrorMessage = resendState.isError 
    ? (authError || "Failed to resend code") 
    : "";
  const challengeSuccessMessage = confirmationState.isSuccess 
    ? "Your email has been verified successfully!" 
    : "";
  const resendCodeSuccessMessage = resendState.isSuccess 
    ? `Verification code sent to ${userEmail || 'your email'}` 
    : "";

  // Get user email from various sources
  useEffect(() => {
    const getUserEmail = () => {
      // First, try pending email from Redux
      if (pendingEmail) {
        return pendingEmail;
      }

      // Try to get email from localStorage (from registration)
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          return user.email || user.username || "";
        } catch (error) {
        }
      }
      
      // Try to get from current user account
      const userAccount = getUserAccount();
      if (userAccount) {
        return userAccount.email || userAccount.username || "";
      }
      
      // Try to get from session storage or other sources
      const pendingEmailStorage = sessionStorage.getItem('pendingConfirmationEmail');
      return pendingEmailStorage || "";
    };

    const email = getUserEmail();
    setUserEmail(email);
  }, [pendingEmail]);

  // Check if user is already verified and redirect if needed
  useEffect(() => {
    const session = getAccountSession();
    const user = getUserAccount();
    
    if (session && user) {
      navigate("/practice");
    }
  }, [navigate]);

  // Enhanced redirect logic with countdown and animation
  useEffect(() => {
    if (confirmationState.isSuccess) {
      setShowSuccessAnimation(true);
      
      // Start countdown
      let countdown = 3;
      setRedirectCountdown(countdown);
      
      const countdownInterval = setInterval(() => {
        countdown -= 1;
        setRedirectCountdown(countdown);
        
        if (countdown <= 0) {
          clearInterval(countdownInterval);
          navigate("/admin/login");
        }
      }, 1000);
      
      return () => clearInterval(countdownInterval);
    }
  }, [confirmationState.isSuccess, navigate]);

  // Enhanced resend code handler using Redux
  const handleResendCode = () => {
    
    if (userEmail) {
      dispatch(resendConfirmationCode({ email: userEmail }));
    } else {
      // Fallback - try to resend without email (will use stored pending email)
      dispatch(resendConfirmationCode({}));
    }
    
    // Clear any existing confirmation errors
    dispatch(clearConfirmationError());
  };

  // Enhanced submit handler using Redux
  const handleSubmit = (data: ChallengeCredentials) => {
    
    // Validate OTP format before submission
    if (!/^\d{6}$/.test(data.otp)) {
      console.error("Invalid OTP format");
      return;
    }
    
    // Dispatch confirmSignUp with email and confirmation code
    dispatch(confirmSignUp({
      email: userEmail,
      confirmationCode: data.otp
    }));
  };

  // Enhanced alert dismissal using Redux
  const handleDismissAlert = () => {
    dispatch(clearConfirmationError());
  };

  // Show preloader during verification process
  if (confirmationState.isLoading && !showSuccessAnimation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <Preloader />
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-4 text-lg font-medium text-gray-700"
          >
            Verifying your email...
          </motion.p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
      {/* <ChallengeForm
        isResendingCode={resendState.isLoading}
        isLoading={confirmationState.isLoading}
        errorMessage={challengeErrorMessage || resendCodeErrorMessage}
        successMessage={challengeSuccessMessage || resendCodeSuccessMessage}
        onDismissAlert={handleDismissAlert}
        onResendCode={handleResendCode}
        onSubmit={handleSubmit}
        userEmail={userEmail}
      /> */}
      {/* Success Animation Overlay */}
      <AnimatePresence>
        {showSuccessAnimation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 20 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-2xl"
            >
              {/* Success Icon */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, duration: 0.6, type: "spring", stiffness: 200 }}
                className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full mb-6 shadow-lg"
              >
                <CheckCircle className="w-10 h-10 text-white" />
              </motion.div>

              {/* Success Message */}
              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="text-2xl font-bold text-gray-900 mb-2"
              >
                Email Verified Successfully!
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="text-gray-600 mb-6"
              >
                Your account has been verified. You'll be redirected to the login page shortly.
              </motion.p>

              {/* Countdown */}
              {redirectCountdown !== null && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.5 }}
                  className="flex items-center justify-center gap-2 text-indigo-600 font-medium"
                >
                  <span>Redirecting in {redirectCountdown} seconds</span>
                  <ArrowRight className="w-4 h-4" />
                </motion.div>
              )}

              {/* Progress Bar */}
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: "100%" }}
                transition={{ duration: 3, ease: "linear" }}
                className="mt-4 h-1 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full"
              />

              {/* Manual Redirect Button */}
              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.5 }}
                onClick={() => navigate("/admin/login")}
                className="mt-6 px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors duration-200"
              >
                Continue to Login
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
